import { useEffect, useRef } from "react";
// 3rd parties
import L from "leaflet";
import moment from "moment";

// owner
import CommonMapReact from "@containers/CommonMapReact/CommonMapReact";
import TowerHarvestedIcon from "@images/map/Cell_tower_harvested.svg";
import TowerLocationiqIcon from "@images/map/Cell_tower_locationiq.svg";
import TowerOpencellIcon from "@images/map/Cell_tower_opencellid.svg";
import TowerHereIcon from "@images/map/Cell_tower_here.svg";
import TowerLiveLocationiqIcon from "@images/map/Cell_tower_live_locationiq.svg";
import TowerLiveOpencellIcon from "@images/map/Cell_tower_live_opencellid.svg";
import TowerLiveHereIcon from "@images/map/Cell_tower_live_here.svg";
import WifiIcon from "@images/map/Wi-Fi.svg";
import StopIcon from "@images/map/stop.svg";

import styled from "styled-components";
import MarkerIcon from "@images/mapbox/images/marker-icon.png";

const Style = styled.div`
  ul {
    padding-left: 1rem;
  }
`;

interface Coordinate {
  x: number;
  y: number;
}

interface TrustedLocation {
  lat: number;
  lng: number;
  source: string;
  timestamp: string;
}

interface Device {
  serial?: string;
}

interface LacTower {
  cellId?: number;
  lat?: number;
  lng?: number;
  observationCount?: number;
  confidence?: string;
}

interface CountryBox {
  iso: string;
  name: string;
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
  nearest: boolean;
}

interface MismatchItem {
  id?: number;
  wifiHull?: Coordinate[];
  wifiLat?: number;
  wifiLng?: number;
  cellLat?: number;
  cellLng?: number;
  wifiProvider?: string;
  macs?: string[];
  type?: string;
  distance?: number;
  countryBoxes?: CountryBox[];
  trustedLocations?: TrustedLocation[];
  device?: Device;
  harvestedLat?: number;
  harvestedLng?: number;
  harvestedObservationCount?: number;
  harvestedConfidence?: string;
  lacTowers?: LacTower[];
}

interface ClickLocation {
  latitude: number;
  longitude: number;
}

interface UpdateWifiCellularMismatchMapProps {
  items: MismatchItem[];
  onClick?: (location: ClickLocation) => void;
  showWifi: boolean;
  showLocationiq: boolean;
  showOpencell: boolean;
  showHere: boolean;
  showAdjacentTrustedPoints: boolean;
  showHarvestedCellTower: boolean;
  showHarvestedLac: boolean;
}

function UpdateWifiCellularMismatchMap({
  items,
  onClick,
  showWifi,
  showLocationiq,
  showOpencell,
  showHere,
  showAdjacentTrustedPoints,
  showHarvestedCellTower,
  showHarvestedLac,
}: UpdateWifiCellularMismatchMapProps) {
  const map = useRef<L.Map | null>(null);
  const wifiHull = useRef<L.Polygon | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const wifiLayer = useRef<L.LayerGroup | null>(null);
  const locationiqLayer = useRef<L.LayerGroup | null>(null);
  const opencellLayer = useRef<L.LayerGroup | null>(null);
  const hereLayer = useRef<L.LayerGroup | null>(null);
  const trustedPointsLayer = useRef<L.LayerGroup | null>(null);
  const harvestedCellTowerLayer = useRef<L.LayerGroup | null>(null);
  const harvestedLacLayer = useRef<L.LayerGroup | null>(null);
  const countryLayer = useRef<L.LayerGroup | null>(null);
  const onClickRef = useRef(onClick);

  // Keep onClick ref updated
  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  // Effect for setting up map data and markers (only when items change)
  useEffect(() => {
    const mapInstance = map.current;
    if (!items || items.length === 0) return;
    if (!mapInstance) return;

    // Remove previous data layers only (preserve user-placed marker)
    if (wifiLayer.current) {
      mapInstance.removeLayer(wifiLayer.current);
    }
    if (locationiqLayer.current) {
      mapInstance.removeLayer(locationiqLayer.current);
    }
    if (opencellLayer.current) {
      mapInstance.removeLayer(opencellLayer.current);
    }
    if (hereLayer.current) {
      mapInstance.removeLayer(hereLayer.current);
    }
    if (trustedPointsLayer.current) {
      mapInstance.removeLayer(trustedPointsLayer.current);
    }
    if (harvestedCellTowerLayer.current) {
      mapInstance.removeLayer(harvestedCellTowerLayer.current);
    }
    if (harvestedLacLayer.current) {
      mapInstance.removeLayer(harvestedLacLayer.current);
    }
    if (countryLayer.current) {
      mapInstance.removeLayer(countryLayer.current);
    }
    if (wifiHull.current) {
      mapInstance.removeLayer(wifiHull.current);
    }

    // DO NOT remove marker.current - preserve user-selected location

    wifiLayer.current = L.layerGroup();
    locationiqLayer.current = L.layerGroup();
    opencellLayer.current = L.layerGroup();
    hereLayer.current = L.layerGroup();
    trustedPointsLayer.current = L.layerGroup();
    harvestedCellTowerLayer.current = L.layerGroup();
    harvestedLacLayer.current = L.layerGroup();
    countryLayer.current = L.layerGroup();

    const bounds = L.latLngBounds([]);
    const allCoordinates: [number, number][] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.wifiHull && item.wifiHull.length > 1) {
        allCoordinates.push(...item.wifiHull.map((c) => [c.x, c.y] as [number, number]));
      }

      // WiFi marker (tooltip exposes the scanned MAC addresses)
      if (item.wifiLat && item.wifiLng) {
        const wifiMarker = new L.Marker([item.wifiLat, item.wifiLng], {
          icon: L.icon({ iconUrl: WifiIcon, iconAnchor: [23, 35] }),
        });
        if (item.macs && item.macs.length > 0) {
          wifiMarker.bindTooltip(`WiFi<br/>${item.macs.join("<br/>")}`);
        }
        wifiMarker.addTo(wifiLayer.current);
        bounds.extend(wifiMarker.getLatLng());
      }

      // Tower marker
      if (item.cellLat && item.cellLng) {
        let targetLayer: L.LayerGroup | null = null;
        let iconUrl = "";
        const isLive = !item.id;

        if (item.wifiProvider === "locationIQ") {
          targetLayer = locationiqLayer.current;
          iconUrl = isLive ? TowerLiveLocationiqIcon : TowerLocationiqIcon;
        } else if (item.wifiProvider === "openCellId") {
          targetLayer = opencellLayer.current;
          iconUrl = isLive ? TowerLiveOpencellIcon : TowerOpencellIcon;
        } else if (item.wifiProvider === "here") {
          targetLayer = hereLayer.current;
          iconUrl = isLive ? TowerLiveHereIcon : TowerHereIcon;
        }

        if (targetLayer) {
          const label = isLive ? `(${item.wifiProvider} - live)` : `(${item.wifiProvider})`;
          const towerMarker = new L.Marker([item.cellLat, item.cellLng], {
            icon: L.icon({
              iconUrl,
              iconAnchor: [23, 35],
            }),
          }).bindTooltip(label);
          towerMarker.addTo(targetLayer);
          bounds.extend(towerMarker.getLatLng());
        }
      }
    }

    // Add trusted points markers
    const trustedPoints = items.flatMap((item) =>
      (item.trustedLocations || []).map((tp) => ({
        latitude: tp.lat,
        longitude: tp.lng,
        locationSource: tp.source,
        createdAt: tp.timestamp,
        deviceSerial: item.device?.serial,
      }))
    );

    trustedPoints.forEach((point) => {
      if (point.latitude && point.longitude) {
        const trustedMarker = new L.Marker([point.latitude, point.longitude], {
          icon: L.icon({
            iconUrl: StopIcon,
            iconAnchor: [12, 12],
          }),
        }).bindTooltip(
          `Source: (${point.locationSource})<br/>` +
            `Device: ${point.deviceSerial}<br/>` +
            `Time: ${moment(point.createdAt).format("lll")}`
        );
        if (trustedPointsLayer.current) {
          trustedMarker.addTo(trustedPointsLayer.current);
        }
        bounds.extend(trustedMarker.getLatLng());
      }
    });

    // Add harvested cell tower marker (from first item, same for all)
    const firstItem = items[0];
    if (firstItem?.harvestedLat && firstItem?.harvestedLng) {
      const harvestedMarker = new L.Marker(
        [firstItem.harvestedLat, firstItem.harvestedLng],
        {
          icon: L.icon({
            iconUrl: TowerHarvestedIcon,
            iconAnchor: [23, 35],
          }),
        }
      ).bindTooltip(
        `Harvested cell tower<br/>` +
          `Observations: ${firstItem.harvestedObservationCount || "?"}<br/>` +
          `Confidence: ${firstItem.harvestedConfidence || "?"}`
      );
      if (harvestedCellTowerLayer.current) {
        harvestedMarker.addTo(harvestedCellTowerLayer.current);
      }
      bounds.extend(harvestedMarker.getLatLng());
    }

    // Add harvested LAC tower markers
    const lacTowers = firstItem?.lacTowers || [];
    lacTowers.forEach((tower) => {
      if (tower.lat && tower.lng) {
        const lacMarker = L.circleMarker([tower.lat, tower.lng], {
          radius: 6,
          color: "#7b1fa2",
          fillColor: "#7b1fa2",
          fillOpacity: 0.6,
          weight: 2,
        }).bindTooltip(
          `LAC tower (cell: ${tower.cellId || "?"})<br/>` +
            `Observations: ${tower.observationCount || "?"}<br/>` +
            `Confidence: ${tower.confidence || "?"}`
        );
        if (harvestedLacLayer.current) {
          lacMarker.addTo(harvestedLacLayer.current);
        }
        bounds.extend(lacMarker.getLatLng());
      }
    });

    // COUNTRY mismatch: no tower to show - highlight the MCC's countries
    // instead, connect the WiFi fix to the nearest one and fit both in view
    const countryBoxes = firstItem?.countryBoxes || [];
    if (countryBoxes.length > 0 && countryLayer.current) {
      countryBoxes.forEach((box) => {
        const rectangle = L.rectangle(
          [
            [box.minLat, box.minLng],
            [box.maxLat, box.maxLng],
          ],
          box.nearest
            ? { color: "#0071a1", weight: 2, fillOpacity: 0.08 }
            : { color: "#0071a1", weight: 1, dashArray: "6", fillOpacity: 0.04 }
        ).bindTooltip(`${box.name} (${box.iso})${box.nearest ? " - nearest" : ""}`);
        if (countryLayer.current) {
          rectangle.addTo(countryLayer.current);
        }
      });

      const nearestBox = countryBoxes.find((box) => box.nearest);
      if (nearestBox && firstItem.wifiLat && firstItem.wifiLng) {
        // clamp the wifi point onto the nearest box = the measured distance
        const clampedLat = Math.min(
          Math.max(firstItem.wifiLat, nearestBox.minLat),
          nearestBox.maxLat
        );
        const clampedLng = Math.min(
          Math.max(firstItem.wifiLng, nearestBox.minLng),
          nearestBox.maxLng
        );
        const line = L.polyline(
          [
            [firstItem.wifiLat, firstItem.wifiLng],
            [clampedLat, clampedLng],
          ],
          { color: "#0071a1", weight: 2, dashArray: "8" }
        );
        if (firstItem.distance != null) {
          line.bindTooltip(`~${Math.round(firstItem.distance / 1000)} km`, {
            permanent: true,
            direction: "center",
          });
        }
        if (countryLayer.current) {
          line.addTo(countryLayer.current);
        }
        bounds.extend([
          [nearestBox.minLat, nearestBox.minLng],
          [nearestBox.maxLat, nearestBox.maxLng],
        ]);
      }

      countryLayer.current.addTo(mapInstance);
    }

    // Create a single polygon with all coordinates
    if (allCoordinates.length > 0) {
      wifiHull.current = L.polygon(allCoordinates, {
        fillOpacity: 0.3,
        weight: 1,
        opacity: 0.3,
      }).addTo(mapInstance);
      bounds.extend(wifiHull.current.getBounds());
    }

    if (bounds.isValid()) {
      mapInstance.fitBounds(bounds, { animate: true, maxZoom: 15 });
    }

    // Set up map click handler (only once when items change)
    const handleMapClick = function (event: L.LeafletMouseEvent) {
      // Remove only the existing user-placed marker (not other layers)
      if (marker.current) {
        mapInstance.removeLayer(marker.current);
        marker.current = null;
      }

      // Create new marker with smooth animation
      const lat = parseFloat(event.latlng.lat.toFixed(6));
      const lng = parseFloat(event.latlng.lng.toFixed(6));

      marker.current = new L.Marker([lat, lng], {
        icon: L.icon({
          iconUrl: MarkerIcon,
          iconAnchor: [13, 41],
        }),
        draggable: true,
        opacity: 0,
      });

      // Add marker with fade-in effect
      marker.current
        .bindTooltip(`(${lat}, ${lng})`, {
          permanent: false,
          direction: "top",
          offset: [0, -10],
        })
        .addTo(mapInstance);

      // Smooth fade-in animation
      setTimeout(() => {
        if (marker.current) {
          marker.current.setOpacity(1);
        }
      }, 50);

      // Handle drag end with debounce
      let dragTimeout: ReturnType<typeof setTimeout>;
      marker.current.on("dragend", function (dragEvent: L.DragEndEvent) {
        clearTimeout(dragTimeout);
        dragTimeout = setTimeout(() => {
          const newLat = parseFloat((dragEvent.target as L.Marker).getLatLng().lat.toFixed(6));
          const newLng = parseFloat((dragEvent.target as L.Marker).getLatLng().lng.toFixed(6));

          if (marker.current) {
            marker.current.bindTooltip(`(${newLat}, ${newLng})`, {
              permanent: false,
              direction: "top",
              offset: [0, -10],
            });
          }

          if (onClickRef.current) {
            onClickRef.current({
              latitude: newLat,
              longitude: newLng,
            });
          }
        }, 100);
      });

      // Call onClick with short delay for UI feedback
      setTimeout(() => {
        if (onClickRef.current) {
          onClickRef.current({
            latitude: lat,
            longitude: lng,
          });
        }
      }, 50);
    };

    mapInstance.off("click"); // Remove any existing click handlers
    mapInstance.on("click", handleMapClick);

    // Clean up on unmount
    return () => {
      if (wifiLayer.current) mapInstance.removeLayer(wifiLayer.current);
      if (locationiqLayer.current)
        mapInstance.removeLayer(locationiqLayer.current);
      if (opencellLayer.current) mapInstance.removeLayer(opencellLayer.current);
      if (hereLayer.current) mapInstance.removeLayer(hereLayer.current);
      if (trustedPointsLayer.current)
        mapInstance.removeLayer(trustedPointsLayer.current);
      if (harvestedCellTowerLayer.current)
        mapInstance.removeLayer(harvestedCellTowerLayer.current);
      if (harvestedLacLayer.current)
        mapInstance.removeLayer(harvestedLacLayer.current);
      if (countryLayer.current) mapInstance.removeLayer(countryLayer.current);
      if (wifiHull.current) mapInstance.removeLayer(wifiHull.current);
      mapInstance.off("click", handleMapClick);
    };
  }, [items]); // Remove onClick from dependencies to prevent unnecessary rerenders

  // Separate effect for layer visibility (preserves marker)
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    // Remove all layers from map first
    if (wifiLayer.current && mapInstance.hasLayer(wifiLayer.current)) {
      mapInstance.removeLayer(wifiLayer.current);
    }
    if (wifiHull.current && mapInstance.hasLayer(wifiHull.current)) {
      mapInstance.removeLayer(wifiHull.current);
    }
    if (
      locationiqLayer.current &&
      mapInstance.hasLayer(locationiqLayer.current)
    ) {
      mapInstance.removeLayer(locationiqLayer.current);
    }
    if (opencellLayer.current && mapInstance.hasLayer(opencellLayer.current)) {
      mapInstance.removeLayer(opencellLayer.current);
    }
    if (hereLayer.current && mapInstance.hasLayer(hereLayer.current)) {
      mapInstance.removeLayer(hereLayer.current);
    }
    if (
      trustedPointsLayer.current &&
      mapInstance.hasLayer(trustedPointsLayer.current)
    ) {
      mapInstance.removeLayer(trustedPointsLayer.current);
    }
    if (
      harvestedCellTowerLayer.current &&
      mapInstance.hasLayer(harvestedCellTowerLayer.current)
    ) {
      mapInstance.removeLayer(harvestedCellTowerLayer.current);
    }
    if (
      harvestedLacLayer.current &&
      mapInstance.hasLayer(harvestedLacLayer.current)
    ) {
      mapInstance.removeLayer(harvestedLacLayer.current);
    }

    // Add layers back based on visibility settings
    if (showWifi && wifiLayer.current) {
      wifiLayer.current.addTo(mapInstance);
    }
    if (showWifi && wifiHull.current) {
      wifiHull.current.addTo(mapInstance);
    }
    if (showLocationiq && locationiqLayer.current) {
      locationiqLayer.current.addTo(mapInstance);
    }
    if (showOpencell && opencellLayer.current) {
      opencellLayer.current.addTo(mapInstance);
    }
    if (showHere && hereLayer.current) {
      hereLayer.current.addTo(mapInstance);
    }
    if (showAdjacentTrustedPoints && trustedPointsLayer.current) {
      trustedPointsLayer.current.addTo(mapInstance);
    }
    if (showHarvestedCellTower && harvestedCellTowerLayer.current) {
      harvestedCellTowerLayer.current.addTo(mapInstance);
    }
    if (showHarvestedLac && harvestedLacLayer.current) {
      harvestedLacLayer.current.addTo(mapInstance);
    }
  }, [
    showWifi,
    showLocationiq,
    showOpencell,
    showHere,
    showAdjacentTrustedPoints,
    showHarvestedCellTower,
    showHarvestedLac,
  ]);

  return (
    <Style>
      <CommonMapReact
        mapRef={map}
        height="600px"
        options={{
          zoomPosition: "topright",
          hideCurrentLocation: true,
          hideFullScreen: true,
          showGeozoneLayer: true,
          showNetworkLayer: true,
          searchBox: true,
        }}
      />
    </Style>
  );
}

export default UpdateWifiCellularMismatchMap;
